import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql, Script } from 'gatsby';
import forEach from 'lodash/forEach';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ExitIntent from './exit-intent';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
// material icons
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import PinterestIcon from '@mui/icons-material/Pinterest';

import Replay30Icon from '@mui/icons-material/Replay30';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { Trans, Link, useI18next } from 'gatsby-plugin-react-i18next';

import iconVisa from '../../images/visa.svg';
import iconMastercard from '../../images/mastercard.svg';
import iconAmex from '../../images/amex.svg';
import iconDiners from '../../images/diners.svg';
import iconDiscover from '../../images/discover.svg';
import iconJcb from '../../images/jcb.svg';
import iconPaypal from '../../images/paypal.png';
import Subscriber from './subscriber';

const getURl = (key: string, value: string): string => {
  if (key === 'twitter') return `https://twitter.com/${value}`;
  if (key === 'facebook') return `https://facebook.com/${value}`;
  if (key === 'instagram') return `https://instagram.com/${value}`;
  if (key === 'pinterest') return `https://pinterest.com/${value}`;
  if (key === 'youtube') {
    if (value.startsWith('@')) {
      return `https://www.youtube.com/${value}`;
    } else {
      return `https://www.youtube.com/channel/${value}`;
    }
  }
  return '';
};

const Footer = () => {
  const {
    site: { siteMetadata },
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          shop {
            name
            support
            address
          }
          social {
            youtube
            instagram
            facebook
            pinterest
            twitter
          }
          paymentMethods
          footerNavigation {
            menu {
              type
              displayName
              subMenu {
                link
                displayName
              }
            }
          }
        }
      }
    }
  `);

  const { language, originalPath } = useI18next();
  const [subscriberOpen, setSubscriberOpen] = useState(false);
  const handleSubscriberOpen = () => {
    setSubscriberOpen(true);
  };

  const handleSubscriberClose = () => {
    setSubscriberOpen(false);
  };

  const socialMedia: React.ReactNode[] = [];
  forEach(siteMetadata.social, (value: string, key: string) => {
    if (!value) return;
    switch (key) {
      case 'youtube':
        socialMedia.push(
          <a key={key} href={getURl(key, value)}>
            <IconButton color="primary" aria-label="youtube" size="large">
              <YouTubeIcon />
            </IconButton>
          </a>
        );
        break;
      case 'instagram':
        socialMedia.push(
          <a key={key} href={getURl(key, value)}>
            <IconButton color="primary" aria-label="instagram" size="large">
              <InstagramIcon />
            </IconButton>
          </a>
        );
        break;
      case 'facebook':
        socialMedia.push(
          <a key={key} href={getURl(key, value)}>
            <IconButton color="primary" aria-label="facebook" size="large">
              <FacebookIcon />
            </IconButton>
          </a>
        );
        break;
      case 'pinterest':
        socialMedia.push(
          <a key={key} href={getURl(key, value)}>
            <IconButton color="primary" aria-label="pinterest" size="large">
              <PinterestIcon />
            </IconButton>
          </a>
        );
        break;
      case 'twitter':
        socialMedia.push(
          <a key={key} href={getURl(key, value)}>
            <IconButton color="primary" aria-label="twitter" size="large">
              <TwitterIcon />
            </IconButton>
          </a>
        );
        break;
      default:
        return;
        break;
    }
  });

  useEffect(() => {
    if (document.cookie.indexOf('show_exit_popup=true') < 0) {
      const removeExitIntent = ExitIntent({
        maxDisplays: 100, // default 99999
        eventThrottle: 500, // default 200
        showAfterInactiveSecondsDesktop: undefined, // default 60
        showAfterInactiveSecondsMobile: undefined, // default 40
        showAgainAfterSeconds: 30, // default 10
        onExitIntent: () => {
          setSubscriberOpen(true);
          let expiryDate = new Date(Date.now() + 7 * (1000 * 60 * 60 * 24));
          expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          document.cookie = 'show_exit_popup' + '=true; expires=' + expiryDate.toUTCString();
        },
        debug: false,
      });
      return () => {
        removeExitIntent();
      };
    }
  });

  return (
    <Grid container spacing={0}>
      {/*Warranty icons*/}
      <Grid container spacing={0} sx={{ bgcolor: '#D6D6D6', py: '5px' }}>
        <Grid item xs={3} sx={{ borderRight: 'solid 2px #fff' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar
              alt="30 Day Money Back Guarantee"
              sx={{
                bgcolor: '#D6D6D6',
                color: '#3f3f3f',
                width: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
                height: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
              }}>
              <SettingsBackupRestoreIcon />
            </Avatar>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              <Trans>30 Day Money Back Guarantee</Trans>
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: 'solid 2px #fff' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar
              alt="30 day returns"
              sx={{
                bgcolor: '#D6D6D6',
                color: '#3f3f3f',
                width: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
                height: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
              }}>
              <Replay30Icon />
            </Avatar>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              <Trans>30 Day Returns</Trans>
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: 'solid 2px #fff' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar
              alt="12 month warranty"
              sx={{
                bgcolor: '#D6D6D6',
                color: '#3f3f3f',
                width: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
                height: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
              }}>
              <CheckOutlinedIcon />
            </Avatar>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              <Trans>12 Month Warranty</Trans>
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar
              alt="100% safe and secure checkout"
              sx={{
                bgcolor: '#D6D6D6',
                color: '#3f3f3f',
                width: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
                height: { xs: 14, sm: 16, md: 18, lg: 20, xl: 24 },
              }}>
              <HealthAndSafetyOutlinedIcon />
            </Avatar>
            <Typography variant="caption" sx={{ textAlign: 'center' }}>
              <Trans>100% Safe &amp; Secure Checkout</Trans>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* sign up form */}
      <Grid
        container
        spacing={0}
        sx={{
          bgcolor: '#D6D6D6',
          px: { xs: '5px', sm: '1rem', md: '2rem', lg: '5rem' },
          py: '1rem',
          borderTop: 'solid 2px #fff',
          alignItems: 'center',
        }}>
        <Grid item xs={12} md={7} lg={8}>
          <Typography
            variant="h6"
            sx={{ color: '#000000', pr: '0.5rem', textAlign: { xs: 'center', md: 'right' }, fontSize: '15px', fontWeight: '600' }}>
            Sign up for our newsletter to receive special offers and news.
          </Typography>
        </Grid>
        <Grid item xs={12} md={5} lg={4}>
          <Formik
            initialValues={{ mail: '' }}
            validationSchema={Yup.object({
              email: Yup.string().email('Invalid email address').required('Required'),
            })}
            onSubmit={(values) => {
              // console.log(values);
              handleSubscriberOpen();
            }}>
            {(formik) => (
              <form id="cart" onSubmit={formik.handleSubmit}>
                <FormControl sx={{ width: { xs: '60%', sm: '70%', md: '65%' } }}>
                  <TextField
                    variant="standard"
                    id="email"
                    name="email"
                    size="medium"
                    sx={{ mx: '1rem' }}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    placeholder="Email"
                    inputProps={{ 'aria-label': 'email' }}
                  />
                </FormControl>
                <Button variant="contained" type="submit" color="success" sx={{ ml: '0rem' }}>
                  Subscribe
                </Button>
                <Dialog open={subscriberOpen} onClose={handleSubscriberClose}>
                  <Subscriber email={formik.values.email} />
                </Dialog>
              </form>
            )}
          </Formik>
        </Grid>
      </Grid>

      {/*footer*/}
      <Grid
        container
        spacing={0}
        columns={12}
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        sx={{ padding: { xs: '5px 5px', sm: '5px 5px', md: '1rem 1rem', lg: '1rem 4rem', xl: '1rem 6rem' }, bgcolor: '#000000ee' }}>
        {/*Links*/}
        <Grid item xs={12} sm={5} md={7} lg={8} sx={{ pl: { xs: 0, md: '2rem', lg: '3rem' }, pb: { xs: '0.8rem', md: '0' } }}>
          <Grid
            container
            spacing={0}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
            }}>
            {siteMetadata.footerNavigation.menu.map(({ type, displayName, subMenu }, index) => {
              if (type === 'Link') {
                return (
                  <Grid
                    item
                    xs={6}
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                      color: '#fefefe',
                      textTransform: 'none',
                    }}>
                    <Typography variant="menulist" sx={{ color: '#fffffff3' }}>
                      <Trans>{displayName}</Trans>
                    </Typography>
                    {subMenu.map(({ displayName, link }, menuindex) => {
                      return (
                        <Box
                          key={menuindex}
                          sx={{ fontSize: '0.7rem', p: 0, m: 0, justifyContent: 'flex-start', textTransform: 'none', color: '#ffffffb3' }}>
                          <Link to={`${link}`} language={language} id="footMenu" className="footMenu" style={{ textDecoration: 'none' }}>
                            <Typography variant="menulist" sx={{ color: '#ffffffc3' }}>
                              <Trans>{displayName}</Trans>
                            </Typography>
                          </Link>
                        </Box>
                      );
                    })}
                  </Grid>
                );
              }
            })}
          </Grid>
        </Grid>

        {/*Support*/}
        <Grid
          item
          xs={12}
          sm={7}
          md={5}
          lg={4}
          sx={{
            pl: { xs: 0, sm: '5px', md: '1rem', lg: '2rem' },
            borderLeft: { xs: 'none', sm: 'solid 1px #ffffffc3' },
            lineHeight: 'initial',
          }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
            {siteMetadata.paymentMethods.map((paymentMethod: string) => {
              switch (paymentMethod) {
                case 'visa':
                  return <img loading="lazy" src={iconVisa} alt="visa" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />;
                  break;
                case 'mastercard':
                  return (
                    <img
                      loading="lazy"
                      src={iconMastercard}
                      alt="mastercard"
                      key={paymentMethod}
                      style={{ margin: '1px', height: '20px' }}
                    />
                  );
                case 'amex':
                  return <img loading="lazy" src={iconAmex} alt="amex" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />;
                  break;
                case 'diners':
                  return <img loading="lazy" src={iconDiners} alt="diners" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />;
                case 'discover':
                  return (
                    <img loading="lazy" src={iconDiscover} alt="discover" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />
                  );
                case 'jcb':
                  return <img loading="lazy" src={iconJcb} alt="jcb" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />;
                case 'paypal':
                  return <img loading="lazy" src={iconPaypal} alt="paypal" key={paymentMethod} style={{ margin: '1px', height: '20px' }} />;
                default:
                  return null;
                  break;
              }
            })}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
            {socialMedia.map((entry) => entry)}
          </Box>
          <Box sx={{ m: 0, p: 0 }}>
            <Button
              variant="text"
              href={`mailto:${siteMetadata.shop.support}`}
              sx={{
                p: '0 0',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: '300',
                color: '#ffffffc3',
              }}>
              {siteMetadata.shop.support}
            </Button>
          </Box>
          <Typography variant="menulist" sx={{ color: '#ffffffc3' }}>
            {siteMetadata.shop.address}
          </Typography>
        </Grid>

        {/*Payment*/}
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          sx={{
            pl: { xs: '0px', md: '0rem' },
            mt: { xs: '0.5rem', md: '1.5rem' },
            mb: '0.2rem',
            order: { xs: 1, md: 0 },
            display: 'flex',
            flexDirection: 'row',
            justifyContent: { xs: 'flex-start', sm: 'center' },
          }}>
          <Box
            sx={{
              pl: { xs: 0, sm: '0.5rem' },
              pr: '0.5rem',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: '300',
              color: '#ffffffb3',
              borderRight: { xs: 'solid 1px #ffffffb3' },
            }}>
            <Link to={'/terms-of-service'} language={language} id="footMenu" className="footMenu" style={{ textDecoration: 'none' }}>
              <Typography variant="menulist" sx={{ color: '#ffffffc3' }}>
                <Trans>Terms of Service</Trans>
              </Typography>
            </Link>
          </Box>
          <Box
            sx={{
              px: '0.5rem',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: '300',
              color: '#ffffffb3',
              borderRight: { xs: 'solid 1px #ffffffb3' },
            }}>
            <Link to={'/privacy-policy'} language={language} id="footMenu" className="footMenu" style={{ textDecoration: 'none' }}>
              <Typography variant="menulist" sx={{ color: '#ffffffc3' }}>
                <Trans>Privacy Policy</Trans>
              </Typography>
            </Link>
          </Box>
          <Typography variant="menulist" sx={{ px: '0.5rem', color: '#ffffffc3' }}>
            {`© ${new Date().getFullYear()} ${siteMetadata.shop.name}`}
          </Typography>
        </Grid>
      </Grid>
      <Script
        strategy="idle"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,t,u,n,a,m){w['MauticTrackingObject']=n;
            w[n]=w[n]||function(){(w[n].q=w[n].q||[]).push(arguments)},a=d.createElement(t),
            m=d.getElementsByTagName(t)[0];a.async=1;a.src=u;m.parentNode.insertBefore(a,m)
            })(window,document,'script','https://m.paganidesignwatch.com/mtc.js','mt');
            mt('send', 'pageview', {site_id: 'pagani', tags: 'pagani'});`,
        }}
      />
    </Grid>
  );
};

export default Footer;
